import { useEffect, useState } from "react";
import { Loader } from "@/components/UI/Loader";
import ProfileIcon from "@/common/images/icons/profileIcon.svg";
import CloseIcon from "@/common/images/icons/cross.svg?react";
import { clsx } from "clsx";
import { FILE_TYPE_REGEX, IMAGE_TYPE_MESSAGE, IMAGE_SIZE_MESSAGE, PROFILE_IMAGE_DIMENSION_LIMIT, PROFILE_IMAGE_SIZE_LIMIT } from "./constants";
import { showNotification } from "@/components/notifications/Notifications";
import { resizeImage } from "./helper";
import "./ImageUploader.scss";

interface IImageUploaderProps {
  preview?: string;
  isError: boolean;
  loading: boolean;
  title: string;
  onFileUpload: (file: File) => void;
  minImgWidth?: number;
  minImgHeight?: number;
  id?: string;
}

export const ImageUploader: React.FC<IImageUploaderProps> = (props) => {
  const { preview, isError, title, loading, minImgWidth, minImgHeight, onFileUpload, id } = props;
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    if (isError) {
      setPreviewImage(null);
    }
  }, [isError]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      // check png/jpeg
      if (!FILE_TYPE_REGEX.test(file.type)) {
        showNotification({
          variant: "error",
          title: IMAGE_TYPE_MESSAGE
        });
        return;
      }

      if (file.size > PROFILE_IMAGE_SIZE_LIMIT) {
        showNotification({
          variant: "error",
          title: IMAGE_SIZE_MESSAGE
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const image = new Image();
        image.onload = () => {
          if ((minImgWidth && image.width < minImgWidth) || (minImgHeight && image.height < minImgHeight)) {
            showNotification({
              variant: "error",
              title: `Image should be at least ${minImgWidth} x ${minImgHeight} pixels`
            });
            return;
          }

          // If the image width or height exceeds 1000px, resize it
          if (image.width > PROFILE_IMAGE_DIMENSION_LIMIT || image.height > PROFILE_IMAGE_DIMENSION_LIMIT) {
            resizeImage(file, PROFILE_IMAGE_DIMENSION_LIMIT, PROFILE_IMAGE_DIMENSION_LIMIT, file.type, (resizedFile) => {
              setPreviewImage(reader.result as string);
              onFileUpload(resizedFile as File);
            });
          } else {
            setPreviewImage(reader.result as string);
            onFileUpload(file);
          }
        };
        image.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="ImageUploader">
      <label htmlFor={id ?? "image-input"} className="ImageUploader-Label">
        <div className="ImageUploader-Preview">
          {!preview && !previewImage ? <img src={ProfileIcon} width={35} height={"auto"} alt="upload logo" /> : (
            <div
              className={clsx(
                "ImageUploader-PreviewImage",
                loading && "ImageUploader-PreviewImage_isLoad"
              )}
              data-testid="preview"
            >
              <img src={previewImage || preview} alt="user file" />
              <span><CloseIcon /></span>
              <div className="Loader"><Loader /></div>
            </div>
          )}
        </div>
        <p className="ImageUploader-Title">{title}</p>
      </label>
      <input
        type="file"
        id={id ?? "image-input"}
        data-testid="image-input"
        onChange={handleChange}
        accept="image/png, image/jpeg"
        className="ImageUploader-Input"
      />
    </div>
  );
};
